import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Shared sidebar logic for ShoppingCart pages:
 * - sticky positioning (only when sidebar fits viewport)
 * - mobile floating checkout bar (appears when CTA scrolls out)
 *
 * @param {Array} deps - extra dependencies that may change sidebar height (e.g. discount)
 * @returns {{ sidebarRef, canStick, showMobileBar }}
 */
export default function useCartSidebar(deps = []) {
  const sidebarRef = useRef(null);
  const [canStick, setCanStick] = useState(true);
  const [showMobileBar, setShowMobileBar] = useState(false);

  const checkCanStick = useCallback(() => {
    if (!sidebarRef.current) return;
    setCanStick(window.innerHeight > sidebarRef.current.offsetHeight);
  }, []);

  // Resize + ResizeObserver
  useEffect(() => {
    checkCanStick();
    window.addEventListener('resize', checkCanStick);
    const ro = new ResizeObserver(checkCanStick);
    if (sidebarRef.current) ro.observe(sidebarRef.current);
    return () => {
      window.removeEventListener('resize', checkCanStick);
      ro.disconnect();
    };
  }, [checkCanStick]);

  // Recalc when deps change (e.g. discount applied -> sidebar height changes)
  const depsKey = JSON.stringify(deps);
  useEffect(() => {
    checkCanStick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey, checkCanStick]);

  // Mobile floating bar: show when CTA button scrolls out of viewport
  useEffect(() => {
    const container = sidebarRef.current;
    if (!container) return;
    const ctaBtn = container.querySelector('.sc-summary__cta-btn');
    if (!ctaBtn) return;

    const observer = new IntersectionObserver(
      ([entry]) => setShowMobileBar(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(ctaBtn);
    return () => observer.disconnect();
  }, []);

  return { sidebarRef, canStick, showMobileBar };
}
